/* eslint-disable import/no-cycle */
import {
  Toolbar,
  QuickFilter,
  QuickFilterControl,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  GridApi
} from '@mui/x-data-grid-pro'
import { Dispatch, RefObject, SetStateAction } from 'react'
import { selectValueType } from '@/shared/components/DynamicForm/DynamicForm'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { UseReactToPrintFn } from 'react-to-print'
import styles from './CustomGridToolbar.module.scss'
import IconButton from '../../../IconButton/IconButton'
import Button from '../../../Button/Button'
import { useTranslation } from 'react-i18next'

export type TableRowFilterStaticDataType = {
  key: string
  label: string
  placeholder: string
  options: OptionType[]
  isMultiSelect?: boolean
}

export type TableRowFilterValuesType = {
  key: string
  value: selectValueType
  onChange: (value: selectValueType) => void
}

export interface CustomGridToolbarProps {
  autosizeOptions: { expand: boolean }
  apiRef: RefObject<GridApi | null>
  restoreColumnSizes: boolean
  setRestoreColumnSizes: Dispatch<SetStateAction<boolean>>
  hideColumnFilter?: boolean
  hideRowFilter?: boolean
  handleOnAddItem: () => void
  addItemLabel?: string
  handleOnDeleteItems: (ids: string[]) => void
  selectedRowIds: string[]
  handlePrint?: UseReactToPrintFn
  hideActionsButtons?: boolean
}

function CustomGridToolbar({
  autosizeOptions,
  apiRef,
  restoreColumnSizes,
  setRestoreColumnSizes,
  hideColumnFilter = false,
  hideRowFilter = false,
  handleOnAddItem,
  addItemLabel,
  handleOnDeleteItems,
  selectedRowIds,
  handlePrint,
  hideActionsButtons = false
}: CustomGridToolbarProps) {
  const handleRestoreColumnSizes = () => {
    apiRef.current?.autosizeColumns(autosizeOptions)
    setRestoreColumnSizes(false)
  }

  const handleAddClick = () => {
    handleOnAddItem()
  }

  const { t } = useTranslation()

  const onPrintClick = () => {
    if (handlePrint) {
      handlePrint()
    }
  }

  return (
    <>
      <Toolbar
        style={{
          minHeight: '2.75rem',
          paddingBottom: '0.75rem',
          paddingLeft: 0,
          paddingRight: 0,
          border: 'none'
        }}
      >
        <div className={styles.tableHeader}>
          <div className={styles.tableHeaderButtonContainer}>
            <QuickFilter>
              <QuickFilterControl
                className={styles.quickFilterControl}
                placeholder={t('TABLE.COLUMNS.SEARCH')}
              />
            </QuickFilter>
            {!hideRowFilter && (
              <FilterPanelTrigger
                className={styles.filterPanelTrigger}
                startIcon={<i className="las la-filter icon-big" />}
              />
            )}
            {!hideColumnFilter && (
              <ColumnsPanelTrigger
                className={styles.columnsPanelTrigger}
                startIcon={<i className="las la-th-list icon-big" />}
              />
            )}
          </div>
          <div className={styles.tableHeaderButtonContainer}>
            {handlePrint && (
              <IconButton
                icon="las la-print"
                color="primary"
                onClick={onPrintClick}
                disabled={selectedRowIds.length === 0}
              />
            )}
            {!hideActionsButtons && addItemLabel && (
              <Button
                label={addItemLabel}
                variant="text"
                color="primary"
                icon="las la-plus icon-medium"
                onClick={handleAddClick}
              />
            )}
            {!hideActionsButtons && !addItemLabel && (
              <IconButton
                icon="las la-plus"
                color="primary"
                onClick={handleAddClick}
              />
            )}
            <IconButton
              icon="las la-sync-alt"
              color="primary"
              onClick={handleRestoreColumnSizes}
              disabled={!restoreColumnSizes}
            />
            {!hideActionsButtons ? (
              <IconButton
                icon="las la-trash"
                color="primary"
                onClick={() => {
                  handleOnDeleteItems(selectedRowIds)
                }}
                disabled={selectedRowIds.length === 0}
              />
            ) : null}
          </div>
        </div>
      </Toolbar>
    </>
  )
}

export default CustomGridToolbar
